import mongoose from "mongoose";
import { match } from "@mssi/pssp-shared";

export interface AssetLocationDetail {
  name1?: string;
  name2?: string;
  line1?: string;
  line2?: string;
  city?: string;
  region?: string;
  regionName?: string;
  regionAbbreviation?: string;
  country?: string;
  countryName?: string;
  postalCode?: string;
}

export interface Asset {
  _id: mongoose.Schema.Types.ObjectId;
  _text?: string;
  itsmCustomerId?: string;
  endCustomerAccountId?: string;
  resellerAccountId?: string;
  serviceDeliveryAccountId?: string; // will replace itsmCustomerId
  opportunityId?: string;
  globalOpportunityId?: string;
  deviceId: string;
  serialNumber?: string;
  orderNumber?: string;
  assetNumber?: string;
  contractId?: string;
  assetType?: string;
  assetTypes: string[];
  modelName?: string;
  familyName?: string;
  productNumber?: string;
  location?: string;
  locationDetail?: AssetLocationDetail;
  locationId?: string;
  state?: string;
  substate?: string;
  assignedTo?: string;
  created?: string;
  updated?: string;
  assignedToContactId?: string;
  assignedToEmail?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contact?: any;
  refreshId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leaseStartDate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leaseEndDate?: any;
  resellerCostCenterId?: string;
  resellerCostCenterName?: string;
  endCustomerCostCenterId?: string;
  endCustomerCostCenterName?: string;
  costCenterId?: string;
  costCenterName?: string;
  orderReference?: string,
  images?: string[];
  macAddress?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  installDate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coaDate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  podDate?: any;
  providerContractId?: string;
  providerContractNumber?: string;
}

export const AssetSchema = new mongoose.Schema<Asset>({
  _text: String,
  itsmCustomerId: { type: String, index: true },
  endCustomerAccountId: { type: String, index: true, sparse: true },
  resellerAccountId: { type: String, index: true, sparse: true },
  serviceDeliveryAccountId: { type: String, index: true, sparse: true },
  opportunityId: { type: String, index: true, sparse: true },
  globalOpportunityId: { type: String, index: true, sparse: true },
  deviceId: { type: String, index: true, unique: true },
  serialNumber: { type: String, index: true, unique: false }, // this is DEFINITELY NOT unique
  assetNumber: { type: String, index: true },
  orderNumber: String,
  contractId: String, // not present
  assetType: { type: String, index: true }, // v2
  assetTypes: [String],
  modelName: { type: String, index: true }, // instead of model
  familyName: String,
  productNumber: { type: String, index: true },
  location: String, // location name
  locationDetail: {
    _id: false,
    line1: String,
    line2: String,
    name1: String,
    name2: String,
    city: String,
    region: String,
    regionName: String,
    regionAbbreviation: String,
    country: String,
    countryName: String,
    postalCode: String,
  },
  locationId: String,
  state: { type: String, index: true },
  substate: String,
  assignedTo: String,
  created: String,
  updated: String,
  assignedToContactId: { type: String, index: true },
  assignedToEmail: String,
  /** @deprecated */
  contact: mongoose.Schema.Types.Mixed,
  refreshId: String,
  leaseStartDate: mongoose.Schema.Types.Mixed,
  leaseEndDate: mongoose.Schema.Types.Mixed,
  resellerCostCenterId: { type: String, sparse: true },
  resellerCostCenterName: String,
  endCustomerCostCenterId: { type: String, sparse: true },
  endCustomerCostCenterName: String,
  costCenterId: { type: String, sparse: true },
  costCenterName: String,
  images: [String],
  macAddress: String,
  installDate: mongoose.Schema.Types.Mixed,
  coaDate: mongoose.Schema.Types.Mixed,
  podDate: mongoose.Schema.Types.Mixed,
  providerContractId: { type: String, sparse: true },
  providerContractNumber: String,
});

AssetSchema.index({
  "itsmCustomerId": 1,
  "refreshId": 1,
});

AssetSchema.index({
  "itsmCustomerId": 1,
  "serialNumber": 1,
});

AssetSchema.index({
  "itsmCustomerId": 1,
  "serialNumber": 1,
  "assetType": 1,
});

AssetSchema.index({
  "itsmCustomerId": 1,
  "state": 1,
});

export const props: (keyof Asset)[] = [
  "deviceId",
  "serialNumber",
  "orderNumber",
  "assetNumber",
  "itsmCustomerId",
  "endCustomerAccountId",
  "resellerAccountId",
  "serviceDeliveryAccountId",
  "opportunityId",
  "globalOpportunityId",
  "contractId",
  "assetType",
  "assetTypes",
  "modelName",
  "familyName",
  "productNumber",
  "location",
  "locationDetail",
  "locationId",
  "endCustomerCostCenterId",
  "resellerCostCenterId",
  "endCustomerCostCenterName",
  "costCenterId",
  "costCenterName",
  "orderReference",
  "resellerCostCenterName",
  "state",
  "substate",
  "assignedTo",
  "created",
  "updated",
  "assignedToContactId",
  "assignedToEmail",
  "contact",
  "leaseStartDate",
  "leaseEndDate",
  "macAddress",
  "installDate",
  "coaDate",
  "podDate",
  "providerContractId",
  // providerContractNumber excluded here
];

export const searchProps: (keyof Asset)[] = [
  "deviceId",
  "opportunityId",
  "globalOpportunityId",
  "serialNumber",
  "orderNumber",
  "assetNumber",
  "contractId",
  "assetType",
  "modelName",
  "familyName",
  "productNumber",
  "location",
  "state",
  "substate",
  "assignedTo",
  "assignedToContactId",
  "created",
  "updated",
  "locationDetail",
  "resellerCostCenterName",
  "endCustomerCostCenterName",
  "costCenterName",
  "orderReference",
  "macAddress",
  "locationId",
  "providerContractId",
  "providerContractNumber",
];

export const dateProps: (keyof Asset)[] = [
  "leaseStartDate",
  "leaseEndDate",
  "installDate",
  "coaDate",
  "podDate",
];

export const filterProps: (keyof Asset)[] = [
  "assetType",
  "state",
  "substate",
  "locationDetail.country" as keyof Asset,
];

export function matches(a: Asset, b: Asset): boolean {
  return match(a, b, props);
}

export const assetUtils = {
  props,
  searchProps,
  filterProps,
  dateProps,
  matches,
};
